import React, {useRef} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Tooltip, Progress, Dropdown, Menu} from 'antd';
import styled from 'styled-components';
import {TableStyled} from '../../home-page-main/components/table-styled';
import {TimelineBlock} from '../../otto-page-detail/Component/GbpAudit/AggregatorNetwork/AggregatorNetwork/viewCitationModal';
import {StyledSpin} from '../../otto-page-detail/Utils/styledSpin';
import {openUrl} from '@/utils/router';
import moment from 'moment';
import {faFileArrowDown, faArrowUp, faSearch, faListCheck, faPause, faCheck, faCircle, faSortDown, faArrowUpRightFromSquare} from '@fortawesome/free-solid-svg-icons';
import {useStore} from '@/store/root-store';

const CitationModal = ({
  openedBusinessInModal,
  citationsData,
}) => {
  const {
    localSeoScan: {
      gettingCitationBusinessDetail,
      exportCitationTableDetail,
      loadingExportCitation,
    },
  } = useStore('');

  const DownloadLink = useRef(null);

  const handleExport = async () => {
    const data = await exportCitationTableDetail(openedBusinessInModal?.id);
    if (DownloadLink.current) {
      DownloadLink.current.href = URL.createObjectURL(new Blob([data], {type: 'text/csv'}));
      let fileName = 'file';
      fileName = fileName.replace(/\.(.*)$/, '');
      DownloadLink.current.download = fileName + '_top_pages.csv';
      DownloadLink.current.click();
    }
  };

  const getTooltipTitle = (status, date) => {
    return `${status} - ${moment(date).format('MMM DD, YYYY')}`;
  };

  const extraStatusProgressColors = {
    'Completed': '#2AC155',
    'Paid': '#2AC155',
    'On Hold': '#FFD700',
  };

  const timeLineArr = [
    {
      status: 'Submitted',
      days: 'Days 1-7',
      icon: faArrowUp,
      completelyFilled: ['Completed', 'Complete', 'Live Listing Checks', 'Paid', 'On Hold', 'Finished'].includes(openedBusinessInModal?.campaignStatus),
      partiallyFilled: ['Submitting To Sites', 'Saved'].includes(openedBusinessInModal?.campaignStatus),
      tooltipText: getTooltipTitle('Saved', openedBusinessInModal?.createdAt),
      progressColor: extraStatusProgressColors[openedBusinessInModal?.campaignStatus] || '#2AC155',
    },
    {
      status: 'Quality Assurance',
      days: 'Days 8-14',
      icon: faSearch,
      completelyFilled: ['Completed', 'Complete', 'Live Listing Checks', 'On Hold', 'Finished'].includes(openedBusinessInModal?.campaignStatus),
      partiallyFilled: ['Paid'].includes(openedBusinessInModal?.campaignStatus),
      tooltipText: getTooltipTitle('Paid', openedBusinessInModal?.createdAt),
      progressColor: extraStatusProgressColors[openedBusinessInModal?.campaignStatus] || '#2AC155',
    },
    {
      status: 'Live Checks',
      days: 'Days 22-37',
      icon: faListCheck,
      completelyFilled: ['On Hold', 'Finished'].includes(openedBusinessInModal?.campaignStatus),
      partiallyFilled: ['Live Listing Checks', 'Completed', 'Complete'].includes(openedBusinessInModal?.campaignStatus),
      tooltipText: getTooltipTitle('Live Listing Checks', openedBusinessInModal?.createdAt),
      progressColor: extraStatusProgressColors[openedBusinessInModal?.campaignStatus] || '#2AC155',
    },
    {
      status: ['On Hold'].includes(openedBusinessInModal?.campaignStatus) ? 'On Hold' : 'Completed',
      days: 'Day 42',
      icon: ['On Hold'].includes(openedBusinessInModal?.campaignStatus) ? faPause : faCheck,
      completelyFilled: ['Finished', 'On Hold'].includes(openedBusinessInModal?.campaignStatus),
      partiallyFilled: ['Finished', 'On Hold'].includes(openedBusinessInModal?.campaignStatus),
      tooltipText: getTooltipTitle(['On Hold'].includes(openedBusinessInModal?.campaignStatus) ? 'On Hold' : 'Finished', openedBusinessInModal?.createdAt),
      progressColor: extraStatusProgressColors[openedBusinessInModal?.campaignStatus] || '#2AC155',
    },
  ];
  const citationColumns = [
    {
      title: 'AGGREGATOR / NETWORK',
      dataIndex: 'aggregator',
      key: 'aggregator',
      width: '200px',
      color: '#17181A',
      render: (_, record) => {
        return <div className='website'>{record?.citation}</div>;
      },
    },
    {
      title: 'WEBSITE',
      dataIndex: 'website',
      key: 'website',
      width: '130px',
      color: '#17181A',
      render: (_, record) => {
        return <div className='domain-authority'>{record?.website}</div>;
      },
    },
    {
      title: 'DATE',
      dataIndex: 'date',
      key: 'date',
      color: '#17181A',
      render: (_, record) => {
        const date = moment(record?.date)?.isValid() ? moment(record?.date)?.format('MMM DD, YYYY') : '-';
        return <div className='domain-authority'>{date}</div>;
      },
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      width: '120px',
      color: '#17181A',
      render: (_, record) => {
        return <div className={`status-tag ${record?.status !== 'Live' ? 'gray' : 'green'}`}>
          <FontAwesomeIcon icon={faCircle} color={record?.status === 'Live' ? '#1FAC47' : '#A3A4A4'} fontSize={8} /> {record?.status}
        </div>;
      },
    },
    {
      title: 'NOTES',
      dataIndex: 'notes',
      key: 'notes',
      color: '#17181A',
      render: (_, record) => {
        return (record?.notes || record?.note) ? <div className='notes'>Show
          <Dropdown
            trigger={['click']}
            placement='bottomRight'
            overlay={
              <Menu style={{width: '400px', padding: '10px', borderRadius: '6px', overflow: 'hidden'}}
              >{record?.notes || record?.note}</Menu>
            }>
            <FontAwesomeIcon style={{cursor: 'pointer'}} icon={faSortDown} color='#121212' fontSize={12} />
          </Dropdown>
        </div> : <div style={{textAlign: 'left'}}>-</div>;
      },
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'citation_url',
      color: '#17181A',
      render: (_, record) => {
        return record?.url ? <div className='citation-url' onClick={() => openUrl(record?.url, '_blank')}><FontAwesomeIcon icon={faArrowUpRightFromSquare} color='#2D6CCA' fontSize={16} />View</div> : <div style={{textAlign: 'left'}}>-</div>;
      },
    },
  ];

  return (
    <Wrapper>
      <div className='wrapper'>
        <div className='header'>
          <div style={{display: 'flex', justifyContent: 'space-between'}}>
            <div className='heading'>Data Aggregator Submissions</div>
            <div>
              {!gettingCitationBusinessDetail && <Button
                disabled={loadingExportCitation}
                color='white'
                onClick={() => handleExport()}
              ><FontAwesomeIcon style={{marginRight: 7}} icon={faFileArrowDown} className={''} />
          Export</Button>}
              <a ref={DownloadLink}></a>
            </div>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: 6, marginBottom: 62}}>
            {timeLineArr?.map((item, idx) => (
              <TimelineBlock key={item?.status} isLast={idx !== timeLineArr?.length -1}>
                <div style={{borderRadius: 9999, background: item?.partiallyFilled || item?.completelyFilled ? item?.progressColor : '#A3A4A4', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 28, minHeight: 28, position: 'relative'}}>
                  <FontAwesomeIcon icon={item?.icon} color='#fff' fontSize={14} />
                  <div style={{position: 'absolute', left: 0, bottom: openedBusinessInModal?.campaignStatus === 'On Hold' ? -30 : -50, whiteSpace: 'nowrap'}}>
                    <Tooltip placement='bottom' overlayStyle={{maxWidth: 350}} overlayInnerStyle={{backgroundColor: '#000', borderRadius: 10}} title={item?.partiallyFilled || item?.completelyFilled ? item?.tooltipText : ''}>
                      <div>
                        <div style={{color: '#000', lineHeight: '20px'}}>{item?.status}</div>
                        {openedBusinessInModal?.campaignStatus === 'On Hold' ? '' : <div style={{color: '#4E5156', fontSize: 12}}>{item?.days}</div>}
                      </div>
                    </Tooltip>
                  </div>
                </div>
                <Progress percent={item?.completelyFilled ? 100 : item?.partiallyFilled ? 50 : 0} strokeColor={item?.progressColor} trailColor='rgba(163, 164, 164, 0.2)' type='line' />
              </TimelineBlock>
            ))}
          </div>
          <div className='description'>
            {gettingCitationBusinessDetail ?
              <StyledSpin fontSize={14} color={'#2D6CCA'} /> :
              citationsData?.filter(citation => citation?.status !== 'Omitted')?.length} of {gettingCitationBusinessDetail ?
              <StyledSpin fontSize={14} color={'#2D6CCA'} /> :
              citationsData?.length} citations found for <Domain onClick={() => openUrl(openedBusinessInModal?.websiteAddress, '_blank')}>{openedBusinessInModal?.businessName}</Domain>

            <span className={'campaign-id'}>(campaign id-{openedBusinessInModal?.id})</span>
          </div>
        </div>
        <div className='body-content'>
          <CitationTable
            dataSource={citationsData}
            columns={citationColumns}
            loading={gettingCitationBusinessDetail}
            loadingRows={10}
            pagination={false}
            scroll={{y: '63vh'}}
          />
        </div>
      </div>
    </Wrapper>
  );
};

export default CitationModal;

export const CitationTable = styled(TableStyled)`
  table {
    padding: 0px !important;
  }
  .ant-table {
    padding: 6px !important;
    border-radius: 16px !important;
    border: 1px solid #D2D2D2 !important;
    box-shadow: 0px 3px 2px 0px #00000005;
    overflow: hidden;
  }
  .ant-table-thead {
    border-radius: 16px;
    tr {
      th:first-child {
        border-top-left-radius: 16px !important;
        border-bottom-left-radius: 16px !important;
        padding-left: 10px !important;
        padding-right: 10px !important;
      }
      th:last-child {
        border-top-right-radius: 16px !important;
        border-bottom-right-radius: 16px !important;
      }
      th {
        &.ant-table-cell-scrollbar {
          display: none !important;
        }
        &:nth-last-child(2) {
          border-top-right-radius: 16px !important;
          border-bottom-right-radius: 16px !important;
        }
      }
    }
  }
  .ant-table-tbody {
    tr {
      td:first-child {
        padding: 0px 10px !important;
      }
      .ant-table-cell {
        .citation-value, .type, .notes, .citation-url, .website, .domain-authority {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 400;
          text-align: left;
          color: #121212;
        }
        .status-tag {
          max-width: 110px;
          padding: 6px 10px;
          border-radius: 6px;
          gap: 5px;
          width: fit-content;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 400;

          &.green {
            background-color: rgba(31, 172, 71, 0.1);
          }
          &.gray {
            background-color: rgba(242, 242, 245, 1);
          }
        }
        .notes {
          display: flex;
          align-items: center;
          gap: 5px;
          svg {
            margin-top: -4px;
          }
        }
        .citation-url {
          max-width: 73px;
          border-radius: 6px;
          padding: 6px 10px;
          background-color: rgba(45, 108, 202, 0.13);
          color: #2D6CCA;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
        }
      }
    }
  }
`;

export const Wrapper = styled.div`
  .wrapper {
    .header {
      margin-bottom: 20px;
      .heading {
        font-family: 'Inter', sans-serif;
        font-size: 24px;
        font-weight: 600;
        color: #121212;
      }
      .description {
        display: flex;
        gap: 4px;
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        font-weight: 500;
        color: #4E5156;
        .campaign-id {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 400;
          color: #000000;
          line-height: 20px;
        }
      }
    }
  }
`;

const Domain = styled.span`
  color: #2D6CCA;
  cursor: pointer;
`;


